import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subscription } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';
import { AppConfig } from '../../../app.config';
import { LocalStorageService } from '@core/services/local-storage/local-storage.service';

const TIMER_ONE_STORE_KEY = 'Timer_One_LastAction';
const TIMER_TWO_STORE_KEY = 'Timer_Two_LastAction';
const FIRST_TIMEOUT_KEY='FirstTimeOut';
const SECOND_TIMEOUT_KEY='SecondTimeOut';

@Injectable()
export class NgIdleService implements OnDestroy {
  public static runTimer: boolean;
  public static runSecondTimer: boolean;
  public USER_IDLE_TIMER_VALUE_IN_MIN: number;
  public FINAL_LEVEL_TIMER_VALUE_IN_MIN: number;
  public userIdlenessChecker: BehaviorSubject<string>;

  public secondLevelUserIdleChecker: BehaviorSubject<string>;
  public isPopWindowOpen:boolean;

  public sessionForIdle: Observable<number>;
  private userActivityChangeCallback: ($event) => void;

  public clockForIdle: Observable<number>;
  public clockOneTimerSubscription: Subscription;
  public clockSecondTimerSubscription: Subscription;

  constructor(private zone: NgZone,
              public localStorageService:LocalStorageService) {
    if (!this.userIdlenessChecker) {
      this.userIdlenessChecker = new BehaviorSubject<string>('INITIATE_TIMER');
    }

    if (!this.secondLevelUserIdleChecker) {
      this.secondLevelUserIdleChecker = new BehaviorSubject<string>('INITIATE_SECOND_TIMER');
    }
  }

  public setIdle(): void {
    this.USER_IDLE_TIMER_VALUE_IN_MIN = AppConfig.settings.settings.Session_Idle_Minutes;
  }

  public setTimeout(): void {
    this.FINAL_LEVEL_TIMER_VALUE_IN_MIN = AppConfig.settings.settings.Session_Message_Timeout_Minutes;
  }

  public initilizeSessionTimeout(): void {
    this.setIdle();
    this.setTimeout();
    this.removeSecondActionFromStore();
    NgIdleService.runTimer = true;
    this.FirstTimeOut=false;
    if (this.USER_IDLE_TIMER_VALUE_IN_MIN === 0) {
      this.userIdlenessChecker.thrownError('Please provide USER_IDLE_TIMER_VALUE in minuite');
      return;
    }

    this.reset();
    this.initListener();
    this.initInterval();
  }

  public initilizeSecondSessionTimeout():void{
    this.SecondTimeOut=false;
    this.setTimeout();
    if (this.FINAL_LEVEL_TIMER_VALUE_IN_MIN === 0) {
      this.secondLevelUserIdleChecker.thrownError('Please provide FINAL_LEVEL_TIMER_VALUE_IN_MIN in minuite');
      return;
    }

    this.executeFinalTimer();
  }

  get lastAction(): number {
    return parseInt(this.localStorageService.getItem(TIMER_ONE_STORE_KEY), 10);
  }

  set lastAction(value) {
    this.localStorageService.setItem(TIMER_ONE_STORE_KEY, value.toString());
  }

  set FirstTimeOut(value:boolean){
    this.localStorageService.setItem(FIRST_TIMEOUT_KEY, value);
  }

  get FirstTimeOut():boolean{
    return this.localStorageService.getItem(FIRST_TIMEOUT_KEY)!=null? this.localStorageService.getItem(FIRST_TIMEOUT_KEY):false;
  }

  set SecondTimeOut(value:boolean){
    this.localStorageService.setItem(SECOND_TIMEOUT_KEY, value);
  }

  get SecondTimeOut():boolean{
    return this.localStorageService.getItem(SECOND_TIMEOUT_KEY)!=null? this.localStorageService.getItem(SECOND_TIMEOUT_KEY):false;
  }

  private initListener(): void {
    this.zone.runOutsideAngular(() => {
        this.userActivityChangeCallback = ($event) => this.handleUserActiveState($event);
        window.document.addEventListener('mouseover', this.userActivityChangeCallback.bind(this), true);
    });
  }

  handleUserActiveState(event): void {
    if(!this.FirstTimeOut)
    {
          this.reset();
    }
  }

  public reset(): void {
    this.lastAction = Date.now();
    if (this.userIdlenessChecker) {
      this.userIdlenessChecker.next('RESET_TIMER');
    }
  }

  private initInterval(): void {
    const intervalDuration = 500;
    this.sessionForIdle = interval(intervalDuration).pipe(
      map((tick: number) => {
        return tick;
      }),
      takeWhile(() => NgIdleService.runTimer)
    );

    this.checkSessionTimeOut();
  }

  private checkSessionTimeOut(): void {
    this.clockOneTimerSubscription=this.sessionForIdle.subscribe(() => {
        const now = Date.now();
        const timeleft = this.lastAction + this.USER_IDLE_TIMER_VALUE_IN_MIN * 60 * 1000;
        const diff = timeleft - now;
        const isTimeout = diff <= 0;
        this.userIdlenessChecker.next(`${diff}`);
        if (isTimeout) {
          window.document.removeEventListener('mouseover', this.userActivityChangeCallback, true);
          this.zone.run(() => {

            if (this.userIdlenessChecker) {
                this.userIdlenessChecker.next('START_SECOND_TIMER');
                if(Number.isNaN(this.secondTimerlastAction) && !this.SecondTimeOut)
                {
                    this.secondTimerlastAction=new Date().getTime() / 1000;
                }
            }
            this.userIdlenessChecker.next('INITIATE_TIMER');
            this.FirstTimeOut=true;
            NgIdleService.runTimer=false;
          });
        }
      });
  }


  public removeAllActionFromStore(): void {
    this.localStorageService.removeItem(TIMER_ONE_STORE_KEY);
    this.localStorageService.removeItem(TIMER_TWO_STORE_KEY);
  }

  public removeOneActionFromStore(): void {
    this.localStorageService.removeItem(TIMER_ONE_STORE_KEY);
  }

  public removeSecondActionFromStore(): void {
    this.localStorageService.removeItem(TIMER_TWO_STORE_KEY);
  }

  get secondTimerlastAction(): number {
    return parseInt(this.localStorageService.getItem(TIMER_TWO_STORE_KEY), 10);
  }

  set secondTimerlastAction(value) {
    this.localStorageService.setItem(TIMER_TWO_STORE_KEY, value.toString());
  }

  public executeFinalTimer = () => {
    NgIdleService.runSecondTimer = true;
    this.initializeFinalTimer();
  }

  private initializeFinalTimer(): void {
    const intervalDuration = 500;
    this.clockForIdle = interval(intervalDuration).pipe(
      map((tick: number) => {
        return tick;
      }),
      takeWhile(() => NgIdleService.runSecondTimer)
    );

    this.checkSecondTimer();
  }

  private checkSecondTimer(): void {
    this.clockSecondTimerSubscription=this.clockForIdle
      .subscribe(() => {
      if(this.FirstTimeOut && !Number.isNaN(this.secondTimerlastAction))
      {
        const now =  new Date().getTime() / 1000;
        const timeleft = this.secondTimerlastAction + this.FINAL_LEVEL_TIMER_VALUE_IN_MIN * 60 ;
        const diff = Math.round(timeleft - now+1);
        const isTimeout = diff <= 0;

        if (isTimeout) {
                this.secondLevelUserIdleChecker.next('SECOND_TIMER_STOPPED');
                NgIdleService.runSecondTimer = false;
        }
        else
        {
               this.secondLevelUserIdleChecker.next(`${diff}`);
        }
      }
      else
      {
            if(this.SecondTimeOut)
            {
              this.secondLevelUserIdleChecker.next('SECOND_TIMER_STOPPED');
            }
            else
            {
               this.secondLevelUserIdleChecker.next('SECOND_TIMER_RESET');
            }
            NgIdleService.runSecondTimer = false;
      }
    });
  }


  public initialIdleService():void{
    this.removeAllActionFromStore();
    this.initilizeSessionTimeout();
    this.initilizeSecondSessionTimeout();
  }

  ngOnDestroy(): void {
    if (this.userIdlenessChecker) {
      this.userIdlenessChecker.unsubscribe();
      this.userIdlenessChecker = undefined;
    }

    if (this.secondLevelUserIdleChecker) {
      this.secondLevelUserIdleChecker.unsubscribe();
      this.secondLevelUserIdleChecker = undefined;
    }

    if(this.clockOneTimerSubscription)
    {
      this.clockOneTimerSubscription.unsubscribe();
      this.clockOneTimerSubscription=undefined;
    }

    if(this.clockSecondTimerSubscription)
    {
      this.clockSecondTimerSubscription.unsubscribe();
      this.clockSecondTimerSubscription=undefined;
    }
  }
}
